import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Authenticated, Unauthenticated, useQuery } from 'convex/react';
import { api } from 'convex/_generated/api';
import { useAuthActions } from '@convex-dev/auth/react';
import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faBars } from '@micks-yard/icons/solid/faBars';
import { faUser } from '@micks-yard/icons/solid/faUser';
import {
  buildNavbarItems,
  isNavbarItemActive,
  NAVBAR_HOME_PATH,
} from '@modules/navbar/business_logic';
import { getSignOutButtonProps } from '@modules/settings/sign_out_button_props';
import { text } from '@modules/settings/text';
import { text as sharedText } from '@shared/text';
import { Button } from '@ui/components/button';
import { Popover } from '@ui/components/popover';
import { Separator } from '@ui/components/separator';
import { AppSheet } from '@ui/components/layouts';
import { cn } from '@/src/lib/utils';
import {
  containerPathToWebRoute,
  pathnameToContainerPath,
  webAppRoutes,
} from '@/src/lib/navigation/containerPaths';

const publicAuthPaths = new Set([
  webAppRoutes.login,
  webAppRoutes.signUp,
  webAppRoutes.forgotPassword,
]);

const LOGO_HEIGHT = 56;
const LOGO_WIDTH = Math.round((949 / 418) * LOGO_HEIGHT);

export function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuthActions();
  const isAdmin = useQuery(api.userRoles.queries.isCurrentUserAdmin);

  const currentPath = pathnameToContainerPath(pathname);

  const navItems = useMemo(
    () =>
      buildNavbarItems({ isAdmin: isAdmin === true }).map((item) => ({
        ...item,
        href: containerPathToWebRoute(item.path),
        isActive: isNavbarItemActive(currentPath, item.path),
      })),
    [currentPath, isAdmin]
  );

  if (publicAuthPaths.has(pathname)) {
    return null;
  }

  const brandHref = containerPathToWebRoute(NAVBAR_HOME_PATH);

  const renderNavButton = (
    item: (typeof navItems)[number],
    onNavigate?: () => void,
    fullWidth = false
  ) => (
    <Button
      key={item.href}
      variant="ghost"
      size="sm"
      className={cn(
        fullWidth && 'w-full justify-start',
        item.isActive
          ? 'bg-green-100 text-green-700 hover:bg-green-100 hover:text-green-700'
          : 'text-muted-foreground'
      )}
      onClick={() => {
        navigate(item.href);
        onNavigate?.();
      }}>
      {item.label}
    </Button>
  );

  return (
    <nav className="border-border shadow-app bg-background sticky top-0 z-50 flex h-16 w-full items-center border-b px-4">
      <div className="flex max-w-full min-w-0 flex-1 items-center justify-between">
        <Link to={brandHref} className="min-w-0 shrink" style={{ textDecoration: 'none' }}>
          <div className="flex min-w-0 items-center">
            <img
              src="/images/logo.png"
              alt={sharedText.appName}
              width={LOGO_WIDTH}
              height={LOGO_HEIGHT}
              style={{ height: LOGO_HEIGHT, width: 'auto', maxWidth: '100%' }}
            />
          </div>
        </Link>

        <div className="hidden items-center gap-2 md:flex">
          <Authenticated>{navItems.map((item) => renderNavButton(item))}</Authenticated>

          <Authenticated>
            <Popover open={userMenuOpen} onOpenChange={setUserMenuOpen}>
              <Popover.Trigger asChild>
                <Button variant="ghost" size="icon" aria-label="User menu">
                  <FaIcon icon={faUser} className="size-4" />
                </Button>
              </Popover.Trigger>
              <Popover.Content className="min-w-[180px] p-2">
                <Button
                  {...getSignOutButtonProps(true)}
                  onClick={() => {
                    void signOut();
                    setUserMenuOpen(false);
                  }}>
                  {text.signOut}
                </Button>
              </Popover.Content>
            </Popover>
          </Authenticated>

          <Unauthenticated>
            <Link to={webAppRoutes.login} style={{ textDecoration: 'none' }}>
              <Button size="sm">Sign In</Button>
            </Link>
          </Unauthenticated>
        </div>

        <div className="flex items-center md:hidden">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Toggle menu"
            onClick={() => setIsMenuOpen(true)}>
            <FaIcon icon={faBars} className="size-4" />
          </Button>
        </div>
      </div>

      <AppSheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <Authenticated>
          {navItems.map((item) => renderNavButton(item, () => setIsMenuOpen(false), true))}
          <Separator className="my-2" />
          <Button
            {...getSignOutButtonProps(true)}
            onClick={() => {
              void signOut();
              setIsMenuOpen(false);
            }}>
            {text.signOut}
          </Button>
        </Authenticated>
        <Unauthenticated>
          <Link
            to={webAppRoutes.login}
            style={{ textDecoration: 'none' }}
            onClick={() => setIsMenuOpen(false)}>
            <Button className="w-full">Sign In</Button>
          </Link>
        </Unauthenticated>
      </AppSheet>
    </nav>
  );
}
